/**
 * seedSelectionsFromPrefill
 *
 * Seeds the client-facing per-unit `decisions`/`selectedPeriods` state from
 * the version payload's `carryForwardPrefill` (offer-decision-carry-forward).
 * Inverts `mergeSelectedPeriodsToRanges`: for each `ACCEPTED` pre-fill entry,
 * a unit's row is marked 'accepted' and its `selectedPeriods` are derived by
 * including every SHARED AXIS period (`ClientOfferReviewPeriod[]`, the SAME
 * axis `mergeSelectedPeriodsToRanges` re-derives ranges from on submit) fully
 * contained within one carried `{from, to}` range — never a raw
 * `AvailabilityPeriodCell` id. A raw server cell's own granularity can be
 * coarser or finer than the display axis, so an id derived from it may not
 * exist in the axis at all, or may cover less of the carried range than the
 * axis does; either way `mergeSelectedPeriodsToRanges` then re-derives a
 * shorter range than what was actually carried, and the server's
 * `assertNoLockedViolations` rejects a locked unit's submission with 422
 * LOCKED_DECISION_MODIFIED even though the client never touched it (bug fix:
 * the round trip must stay in one id space end to end). A `REJECTED` entry —
 * the viewed version's OWN already-persisted decision, never produced by
 * cross-version carry-forward (client-decisions-rehydrate-on-reload bug fix)
 * — marks the row 'rejected' with no `selectedPeriods`. `UNDECIDED` entries
 * and mediaUnitIds with no matching current row are no-ops — the resulting
 * empty defaults match the pre-existing "undecided" state.
 *
 * Traceability: offer-decision-carry-forward / Scenario: The client view starts
 * with previously-accepted units pre-selected
 */
import type { ClientDecision } from '@ooh/ui/components/client-offer-view';
import type {
  ClientOfferViewUnit,
  CarryForwardPrefill,
  RecordedUnitDecisions,
} from '@ooh/contracts';
import type { ClientOfferReviewPeriod } from '../../client-offer-review';
import { buildReviewPeriodAxisFromEnrichedUnits } from './build-review-periods';

export interface SeededSelections {
  decisions: Record<string, ClientDecision>;
  selectedPeriods: Record<string, string[]>;
  /**
   * Row ids (matching `decisions`/`selectedPeriods` keys) whose accept state and period
   * selection are binding — carried forward from a prior version with unchanged terms AND
   * the unit currently has an active SOLD reservation for this offer (offer-decision-
   * carry-forward / Locked carry-forward entries are enforced on finalize). A carried
   * unit with no reservation, or only SOFT_RESERVATION/HARD_RESERVATION/OPTION, is never
   * in this set even though it still seeds as 'accepted' above.
   * Consumed by useClientOfferSelections to no-op every mutation entry point for these rows.
   */
  lockedRowIds: Set<string>;
}

/**
 * Inverts an `enrichedById` (snapshot row id -> enriched unit) lookup into
 * `mediaUnitId -> row id` — the id space `unitDecisions`/`unit-decisions`
 * responses key by (client-offer-decision-persistence), since a persisted
 * decision must remain resolvable across version regeneration where the
 * row id itself does not survive. Exported so any caller mapping a
 * mediaUnitId-keyed decision list onto snapshot row ids reuses this single
 * lookup-building convention instead of re-deriving it — see
 * offer-version-snapshot-view.tsx's buildDecisionsFromUnitDecisions.
 */
export function buildRowIdByMediaUnitId(
  enrichedById: Map<number, ClientOfferViewUnit>,
): Map<number, string> {
  const rowIdByMediaUnitId = new Map<number, string>();
  for (const [rowId, unit] of enrichedById.entries()) {
    rowIdByMediaUnitId.set(unit.mediaUnitId, String(rowId));
  }
  return rowIdByMediaUnitId;
}

/** ISO-date-string cell boundary → UTC-midnight Date, matching merge-selected-periods.ts. */
function toUtcMidnight(isoDate: string): Date {
  return new Date(`${isoDate}T00:00:00Z`);
}

function derivePeriodIdsContainedInRanges(
  periods: ClientOfferReviewPeriod[],
  ranges: { from: Date; to: Date }[],
): string[] {
  const periodIds: string[] = [];
  for (const period of periods) {
    const periodStart = toUtcMidnight(period.start);
    const periodEnd = toUtcMidnight(period.end);
    const isContained = ranges.some((range) => periodStart >= range.from && periodEnd <= range.to);
    if (isContained) periodIds.push(period.id);
  }
  return periodIds;
}

export interface ReadOnlyRecordedSelections {
  decisions: Record<string, ClientDecision>;
  selectedPeriods: Record<string, string[]>;
}

/**
 * Converts the server's recorded per-unit decisions (client-offer-decision-persistence)
 * into the decisions/selectedPeriods shape the post-acceptance read-only client view
 * renders (client-offer-interactive-view / Post-Acceptance Read-Only Client View). Unlike
 * seedSelectionsFromPrefill above (seeds a WRITABLE in-progress session from carry-forward
 * data), this is a one-shot, read-only hydration with no lockedRowIds — nothing is
 * editable in that view regardless of lock state.
 *
 * Builds the period axis via buildReviewPeriodAxis at 'HALF_MONTH' — the same call the
 * read-only shell itself makes when uncontrolled (see ClientOfferReviewShellProps.granularity
 * and INITIAL_GRANULARITY in use-client-offer-selections.ts) — so a recorded period range
 * resolves to the exact period ids the shell's own strip renders; any mismatch here would
 * silently show zero selected chips for a unit that really has recorded periods.
 *
 * A recorded decision naming a mediaUnitId absent from the current snapshot is omitted
 * (matches "Recorded decision for a nośnik absent from the snapshot is omitted").
 */
export function buildReadOnlyDecisionsFromRecorded(
  recordedDecisions: RecordedUnitDecisions | undefined,
  enrichedById: Map<number, ClientOfferViewUnit>,
  campaignFrom: Date | null,
  campaignTo: Date | null,
  language: string,
): ReadOnlyRecordedSelections {
  const decisions: Record<string, ClientDecision> = {};
  const selectedPeriods: Record<string, string[]> = {};
  if (!recordedDecisions || recordedDecisions.length === 0) {
    return { decisions, selectedPeriods };
  }

  const rowIdByMediaUnitId = buildRowIdByMediaUnitId(enrichedById);
  const { periods } = buildReviewPeriodAxisFromEnrichedUnits(
    enrichedById,
    'HALF_MONTH',
    campaignFrom,
    campaignTo,
    language,
  );

  for (const entry of recordedDecisions) {
    const rowId = rowIdByMediaUnitId.get(entry.mediaUnitId);
    if (rowId == null) continue;

    if (entry.decision === 'REJECTED') {
      decisions[rowId] = 'rejected';
      continue;
    }

    decisions[rowId] = 'accepted';
    selectedPeriods[rowId] = derivePeriodIdsContainedInRanges(periods, entry.periods ?? []);
  }

  return { decisions, selectedPeriods };
}

export function seedSelectionsFromPrefill(
  carryForwardPrefill: CarryForwardPrefill,
  enrichedById: Map<number, ClientOfferViewUnit>,
  periods: ClientOfferReviewPeriod[],
): SeededSelections {
  const decisions: Record<string, ClientDecision> = {};
  const selectedPeriods: Record<string, string[]> = {};
  const lockedRowIds = new Set<string>();
  if (carryForwardPrefill.length === 0) return { decisions, selectedPeriods, lockedRowIds };

  const rowIdByMediaUnitId = buildRowIdByMediaUnitId(enrichedById);

  for (const entry of carryForwardPrefill) {
    if (entry.decision === 'UNDECIDED') continue;
    const rowId = rowIdByMediaUnitId.get(entry.mediaUnitId);
    if (rowId == null) continue;

    if (entry.decision === 'REJECTED') {
      decisions[rowId] = 'rejected';
      continue;
    }

    decisions[rowId] = 'accepted';
    selectedPeriods[rowId] = derivePeriodIdsContainedInRanges(periods, entry.periods ?? []);
    if (entry.locked) lockedRowIds.add(rowId);
  }

  return { decisions, selectedPeriods, lockedRowIds };
}
